import { LocalizedText } from "../i18n/LocalizedCopy";
import { useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useNavigationTranslations from "../i18n/useNavigationTranslations";
import { useI18n } from "../i18n/LanguageContext";

import {
  buildConciergePlan,
  buildTodayActions,
  safeConciergeRoute,
} from "./conciergeEngine";
import SportConciergeJourney from "./SportConciergeJourney";
import "./ConciergeEternua.css";

const EXAMPLES = [
  "Une activité sportive gratuite samedi",
  "Je veux vendre mon vélo",
  "Je cherche une personne de confiance pour garder mon enfant",
  "Organise une sortie à cinq",
];

function ConfirmationDialog({ action, onCancel, onConfirm }) {
  useSourceLanguage();
  const { t } = useI18n();
  const ref = useRef(null);
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="et-concierge-confirm"
      aria-labelledby="et-concierge-confirm-title"
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
    >
      <span className="et-concierge-confirm__mark" aria-hidden="true">◇</span>
      <p>{<LocalizedText source={t("concierge.confirmIntro", "Vérification avant de continuer")} />}</p>
      <h2 id="et-concierge-confirm-title">{action.label}</h2>
      <p>{action.confirmation}</p>
      <div>
        <button type="button" onClick={onCancel}>{<LocalizedText source={t("concierge.cancel", "Revenir")} />}</button>
        <button type="button" className="is-primary" onClick={onConfirm}>{<LocalizedText source={t("concierge.confirm", "Confirmer et ouvrir")} />}</button>
      </div>
    </dialog>
  );
}

function ActionButton({ action, onOpen, isLoggedIn }) {
  useSourceLanguage();
  const { t } = useI18n();
  return (
    <button type="button" className="et-concierge-plan__action" onClick={() => onOpen(action)}>
      <span>
        <small>{action.requiresAuth && !isLoggedIn ? <LocalizedText source={t("concierge.required", "Connexion requise")} /> : action.kind === "prepare" ? <LocalizedText source={t("concierge.confirmStatus", "À confirmer")} /> : <LocalizedText source={t("concierge.direct", "Accès direct")} />}</small>
        <strong>{action.label}</strong>
        <span>{action.description}</span>
      </span>
      <i aria-hidden="true">→</i>
    </button>
  );
}

export function ConciergeTodayActions({ priorities }) {
  useSourceLanguage();
  const { t } = useI18n();
  const { translateLabel } = useNavigationTranslations();
  const actions = buildTodayActions(priorities, translateLabel);
  return (
    <section className="et-today-actions" aria-labelledby="et-today-actions-title">
      <header>
        <div>
          <p>{<LocalizedText source={t("concierge.today", "Trois actions utiles")} />}</p>
          <h2 id="et-today-actions-title">{<LocalizedText source={t("concierge.todayTitle", "Continuer, découvrir ou répondre")} />}</h2>
        </div>
        <span>{<LocalizedText source={t("concierge.todayNote", "Pas de flux infini · uniquement des chemins concrets")} />}</span>
      </header>
      <div className="et-today-actions__grid">
        {actions.map((item) => (
          <Link to={item.route} key={item.id} className={`et-today-action et-today-action--${item.id}`}>
            <span className="et-today-action__icon" aria-hidden="true">{item.icon}</span>
            <span className="et-today-action__copy">
              <small>{item.eyebrow}</small>
              <strong>{item.title}</strong>
              <span>{item.description}</span>
              <b>{item.label} <i aria-hidden="true">→</i></b>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function ConciergeEternua({ isLoggedIn = false, variant = "hero", initialValue = "", guideName = "" }) {
  useSourceLanguage();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const inputRef = useRef(null);
  const [input, setInput] = useState(initialValue);
  const [submitted, setSubmitted] = useState(() => new URLSearchParams(window.location.search).get("concierge") === "groups" ? "Organise une sortie à plusieurs" : "");
  const [pendingAction, setPendingAction] = useState(null);
  const plan = useMemo(() => submitted ? buildConciergePlan(submitted) : null, [submitted]);

  useEffect(() => {
    if (hash === "#lia") {
      inputRef.current?.scrollIntoView?.({ block: "center", behavior: "auto" });
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [hash]);

  const navigateToAction = (selected) => {
    const destination = safeConciergeRoute(selected.route);
    window.dispatchEvent(new CustomEvent("eternua:concierge-action", {
      detail: { intent: plan?.id || "shortcut", action: selected.id },
    }));
    if (selected.requiresAuth && !isLoggedIn) {
      navigate(`/login?next=${encodeURIComponent(destination)}`);
      return;
    }
    navigate(destination);
  };

  const openAction = (selected) => {
    if (selected.requiresConfirmation) {
      setPendingAction(selected);
      return;
    }
    navigateToAction(selected);
  };

  const submit = (event) => {
    event.preventDefault();
    const value = String(input || "").trim();
    if (!value) {
      inputRef.current?.focus();
      return;
    }
    setSubmitted(value);
  };

  const chooseExample = (value) => {
    setInput(value);
    setSubmitted(value);
  };

  return (
    <section className={`et-concierge et-concierge--${variant}`} aria-labelledby={`et-concierge-title-${variant}`}>
      <div className="et-concierge__heading">
        <div>
          <p>{guideName ? `${guideName} · ${t("concierge.guide", "Votre guide Eternua")}` : <LocalizedText source={t("concierge.name", "Concierge Eternua")} />}</p>
          <h2 id={`et-concierge-title-${variant}`}>{<LocalizedText source={t("concierge.need", "De quoi avez-vous besoin aujourd’hui ?")} />}</h2>
        </div>
        <span className="et-concierge__local"><i aria-hidden="true">✓</i>{" "}{<LocalizedText source={t("concierge.prepared", "Guide à réponses préparées")} />}</span>
      </div>

      <form className="et-concierge__form" onSubmit={submit} role="search">
        <label htmlFor={`et-concierge-input-${variant}`} className="home-sr-only">{<LocalizedText source={t("concierge.input", "Décrire votre besoin")} />}</label>
        <span aria-hidden="true">⌕</span>
        <input
          ref={inputRef}
          id={`et-concierge-input-${variant}`}
          value={input}
          onChange={(event) => setInput(event.target.value.slice(0, 240))}
          placeholder={t("concierge.placeholder", "Ex. Je cherche une activité gratuite samedi…")}
          autoComplete="off"
          aria-describedby={`et-concierge-help-${variant}`}
        />
        <button type="submit">{<LocalizedText source={t("concierge.prepare", "Préparer mon parcours")} />}</button>
      </form>

      <div className="et-concierge__examples" aria-label={t("concierge.examples", "Exemples de besoins")}>
        {EXAMPLES.map((example, index) => (
          <button type="button" key={example} onClick={() => chooseExample(example)}>{<LocalizedText source={t(`concierge.example.${index + 1}`, example)} />}</button>
        ))}
      </div>

      <p className="et-concierge__privacy" id={`et-concierge-help-${variant}`}>
        {t("concierge.privacy", "Décrivez votre besoin ou choisissez un exemple. {guide} vous propose des liens à partir de réponses préparées, sans conversation avec une IA. Votre besoin est analysé dans ce navigateur. Les mots de votre recherche peuvent être transmis au service que vous choisissez d’ouvrir.").replace("{guide}", guideName || t("concierge.name", "Le Concierge"))}
      </p>

      {plan ? (
        <div className="et-concierge-plan" role="status" aria-live="polite">
          <header>
            <div>
              <small>{<LocalizedText source={t("concierge.next", "Votre prochain pas")} />}</small>
              <h3>{plan.title}</h3>
              <p>{plan.summary}</p>
            </div>
            {plan.factLabels.length ? (
              <ul aria-label={t("concierge.facts", "Éléments compris")}>
                {plan.factLabels.map((fact) => <li key={fact}>{fact}</li>)}
              </ul>
            ) : null}
          </header>
          <div className="et-concierge-plan__actions">
            {plan.actions.slice(0, 3).map((item) => <ActionButton key={item.id} action={item} onOpen={openAction} isLoggedIn={isLoggedIn} />)}
          </div>
          {["sport_discover", "sport_organize", "outing_organize"].includes(plan.id) ? (
            <SportConciergeJourney
              facts={plan.facts}
              isLoggedIn={isLoggedIn}
              onOpen={(destination) => navigate(safeConciergeRoute(destination))}
            />
          ) : null}
          <p className="et-concierge-plan__truth"><span aria-hidden="true">i</span>{" "}{<LocalizedText source={t("concierge.truth", "Ce guide vous oriente. Aucune publication, réservation, prise de contact ou paiement n’est effectué à votre place.")} />}</p>
        </div>
      ) : null}

      {pendingAction ? (
        <ConfirmationDialog
          action={pendingAction}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => {
            const selected = pendingAction;
            setPendingAction(null);
            navigateToAction(selected);
          }}
        />
      ) : null}
    </section>
  );
}
